import {
  type ComponentPropsWithoutRef,
  forwardRef,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';
import { cn } from '../lib/cn';
import { Typography } from './typography';

import { Icon } from '@/shared/assets/icons';
import { TooltipElement } from './tooltip';

type TextAreaProps = {
  label?: string;
  helpText?: string;
  error?: boolean;
  errorText?: string;
  rightNode?: ReactNode;
  textAreaClassName?: string;
  required?: boolean;
  bottomGutter?: boolean;
  infoText?: string;
  autoResize?: boolean;
} & ComponentPropsWithoutRef<'textarea'>;

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      error = false,
      required = false,
      bottomGutter = true,
      autoResize = false,
      label,
      style,
      disabled,
      helpText,
      errorText,
      rightNode,
      className,
      textAreaClassName,
      infoText,
      ...textAreaProps
    },
    ref
  ) => {
    const localRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
      if (autoResize && localRef.current) {
        const textarea = localRef.current;
        const adjustHeight = () => {
          textarea.style.height = 'auto';
          textarea.style.height = `${textarea.scrollHeight}px`;
        };

        adjustHeight();
        textarea.addEventListener('input', adjustHeight);

        return () => textarea.removeEventListener('input', adjustHeight);
      }
    }, [autoResize, textAreaProps.value]);

    return (
      <div className={cn('flex w-full flex-col', className)}>
        {label && (
          <div className="flex items-center gap-1 pb-2">
            {required && (
              <Icon name="required" className="text-dust-red-5" size={10} />
            )}
            <Typography>{label}</Typography>
            {!!infoText && <TooltipElement content={infoText} />}
          </div>
        )}

        <div
          style={style}
          className={cn(
            'border-neutral-5 bg-neutral-1 flex w-full gap-1 rounded-xs border px-3 py-2',
            error
              ? 'border-dust-red-5'
              : 'border-neutral-5 focus-within:border-primary-6',
            disabled &&
              'bg-neutral-3 border-neutral-5 cursor-not-allowed border opacity-50'
          )}
        >
          <textarea
            disabled={disabled}
            ref={(node) => {
              localRef.current = node;
              if (typeof ref === 'function') {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            }}
            {...textAreaProps}
            className={cn(
              'w-full resize-none text-[14px] leading-[22px] focus:ring-0 focus:outline-none',
              disabled && 'cursor-not-allowed',
              autoResize && 'overflow-hidden',
              textAreaClassName
            )}
          />
          {rightNode}
        </div>

        <div
          className={cn(
            (bottomGutter ?? helpText ?? error) && 'min-h-6 w-full'
          )}
        >
          {errorText && error && (
            <Typography
              className="text-dust-red-5"
              variant="footnote-description"
            >
              {errorText}
            </Typography>
          )}
          {!error && helpText && (
            <Typography className="opacity-45" variant="footnote-description">
              {helpText}
            </Typography>
          )}
        </div>
      </div>
    );
  }
);

TextArea.displayName = 'UI/TextArea';
